import PaymentProcessCard, { PaymentSectionDataProps } from "@components/integration/PaymentProcessCard";
import PaymentStepsAccordion, { PaymentProcess } from "@components/integration/PaymentStepsAccordion";
import AwardsSection from "@components/new-ui/AwardsSection";
import CaseStudyFeatureSection, { Feature } from "@components/new-ui/CaseStudyFeatureSection";
import ClientLogos from "@components/new-ui/ClientLogos";
import { container_classname } from "@components/new-ui/constants";
import CTASection from "@components/new-ui/CTASection";
import FAQSection, { FAQSectionProps } from "@components/new-ui/FAQSection";
import FeatureInfoCard, { FeatureInfo } from "@components/new-ui/IndustriesSection/FeatureInfoCard";
import Herosection, { HerosectionProps } from "@components/new-ui/IndustriesSection/Herosection";
import IntegrationSection from "@components/new-ui/IntegrationSection";
import NewLayout from "@components/new-ui/Layout";
import ProblemSectionCard, { ProblemSection } from "@components/solution/problemSectionCard";
import React from "react";

const herosectionData: HerosectionProps = {
    chipTitle: "WhatsApp shared team inbox",
    title: "Manage 2X more business chats with WhatsApp Team Inbox software",
    description:
        "Make a note, tag a team member or assign conversations from a unified WhatsApp Team Inbox software. Handle more pings at any time with smart automations and chatbot handoffs.",
    primaryButtonText: "Start Free Trial",
    imageUrl: "https://assets.gallabox.com/gb-home/whatsapp-shared-inbox/shared-team-inbox-hero-image.png",
    showBookACall: true,
};

const featureInfo1: FeatureInfo = {
    mainTitle: "One WhatsApp account, multiple team members",
    subtitle: "Oversee conversations without the complexity of managing separate WhatsApp numbers.",
    imageUrl: "https://assets.gallabox.com/gb-home/whatsapp-shared-inbox/team-members.png",
    featureInfoList: [
        "Access all customer interactions from a single, unified inbox view",
        "Whether it’s 5 or 100 members — get them unique logins with one WhatsApp number",
        "Never miss a message by engaging users instantly with AI-powered bots.",
    ],
};

const featureInfo2: FeatureInfo = {
    mainTitle: "Hit peak productivity with an AI-powered compose box",
    subtitle: "Let your team members do more with less effort with AI. Save their time for more complex customer interactions.",
    imageUrl: "https://assets.gallabox.com/gb-home/whatsapp-shared-inbox/whatsapp-templates.png",
    featureInfoList: [
        "Trigger conversation workflows and choose WhatsApp templates instantly",
        "Rewrite with AI, translate in 100+ languages or use canned responses for instant, accurate replies",
        "Transfer chats between individuals, teams, and bots without disrupting user experience.",
    ],
};

const featureInfo3: FeatureInfo = {
    mainTitle: "Manage teams better and assign conversations diligently",
    subtitle: "Be in control of how you assign incoming WhatsApp messages to different teams.",
    imageUrl: "https://assets.gallabox.com/gb-home/whatsapp-shared-inbox/manage-teams.png",
    featureInfoList: [
        "Assign teams and roles to users for proper role-clarity and hierarchy across the board",
        "Decide what type of conversations go a particular team based on their expertise or specialization",
        "Ensure your reps get a fair shot at hitting their quotas by automatically balancing workloads across teams.",
    ],
};

const ProblemSectionData: ProblemSection = {
    maintitle: "Benefits of using our WhatsApp Team Inbox software ",
    problemSectionCard: [
        {
            imageUrl: "https://assets.gallabox.com/gb-home/solution/solution-icons/message-time.svg",
            title: "View 360° chat history of prospects",
            description:
                "Access the entire conversation history with each customer to maintain context. No matter who picks up the conversation next, they'll have the full picture.",
        },
        {
            imageUrl: "https://assets.gallabox.com/gb-home/solution/solution-icons/share-circle.svg",
            title: "Get cross-functional visibility",
            description: "Team leaders can monitor all customer interactions in real-time, ensuring consistent quality and providing guidance when needed.",
        },
        {
            imageUrl: "https://assets.gallabox.com/gb-home/solution/solution-icons/group-chat.svg",
            title: "Pin high-intent conversations",
            description: "Set up alerts for high-value opportunities that need attention. Add custom tags to every conversation for better segmentation.",
        },
        {
            imageUrl: "https://assets.gallabox.com/gb-home/solution/solution-icons/profile-image.svg",
            title: "Add value with rich media messages",
            description: "Send messages in multiple media formats with reply buttons, PDFs, catalogs, voice messages, bot templates, videos, images, and more.",
        },
    ],
};

const caseStudyFeatureCardData: Array<Feature> = [
    {
        mediaUrl: "https://assets.gallabox.com/gb-home/case-study/dubai-housekeeping.png",
        chiptitle: "SERVICE",
        title: "Gallabox transforms Dubai Housekeeping's journey to service excellence on WhatsApp",
        statsInfoData: [
            { percentage: "25%", label: "Increase in team productivity" },
            { percentage: "47%", label: "Higher CSAT ratings" },
            { percentage: "31%", label: "Growth from WhatsApp" },
        ],
        url: "/case-study/dubai-housekeeping",
    },
    {
        mediaUrl: "https://assets.gallabox.com/gb-home/case-study/thread-bucket-studio/thread-bucket-studio.png",
        chiptitle: "REAL ESTATE",
        title: "Why Thread Bucket Studio chose Gallabox over Wati for seamless team communication",
        statsInfoData: [
            { percentage: "68%", label: "Higher business growth" },
            { percentage: "50%", label: "Rise in employee productivity" },
            { percentage: "30%", label: "Growth in customer base" },
        ],
        url: "/case-study/thread-bucket-studio",
    },
    {
        mediaUrl: "https://assets.gallabox.com/gb-home/whatsapp-shared-inbox/jobstars-group.png",
        chiptitle: "FINANCE",
        title: "Gallabox deflects 60% of Jobstars' customer chats to a multilingual chatbot",
        statsInfoData: [
            { percentage: "2000+", label: "candidates acquired by Bot" },
            { percentage: "10,000", label: "Minutes Saved by Bot" },
            { percentage: "60%", label: "Chats handled by Bot" },
        ],
        url: "/case-study/jobstars-india",
    },
];

const faqData: FAQSectionProps = {
    title: "FAQs about WhatsApp Team Inbox Software",
    faqItems: [
        {
            question: "Can I manage multiple WhatsApp accounts or numbers through a single team inbox?",
            answer: "Yes! In fact, it’s one of the biggest perks of our Gallabox’s WhatsApp team inbox. You can effortlessly manage several WhatsApp Business accounts through a single unified dashboard. You don’t have to jump between different screens or devices to handle various accounts. We've seen our customers seamlessly 30+ different WhatsApp numbers from one place, which makes life easier for their sales and success teams.",
        },
        {
            question: "Is it possible to assign conversations to specific team members in WhatsApp Team Inbox Software?",
            answer: "Yes, and it's super simple! You can manually assign chats to team members or set up automatic routing based on criteria like expertise, availability, team, or time zone. You can even create custom rules to send all product-related queries to your specialists or routing VIP customer queries to your senior account managers.",
        },
        {
            question: "How does WhatsApp Team Inbox Software ensure customer queries are responded to promptly?",
            answer: "Our WhatsApp Team Inbox platform sends real-time notifications for every incoming conversation. You can also flag conversations that are pending from your end, and set SLAs to ensure prompt action. Plus, Gallabox’s chatbots can handle common queries instantly while your team focuses on more complex issues. Many of our customers have cut their response times by over 60% after implementing quick replies and AI chatbots.",
        },
        {
            question: "Can WhatsApp Team Inbox Software be integrated with other customer support tools or CRMs?",
            answer: "Certainly. Our WhatsApp Team Inbox software plays nicely with your existing tech stack. Gallabox connects smoothly with popular CRMs like Zoho and HubSpot; office tools like Google Sheets and Calendly; marketing automation tools like MoEngage and WebEngage; e-commerce platforms like Shopify; payment gateways like Stripe and Razorpay. And a lot more business-critical tools and software. This will enable your team to access customer history and conversation context without switching between too many tabs.",
        },
        {
            question: "How secure is the communication and data handled by WhatsApp Team Inbox Software?",
            answer: "Security is a basic, non-negotiable tenet for Gallabox’s WhatsApp Team Inbox Software. Being an official Meta partner, WhatsApp Business API’s end-to-end encryption extends to us as well. In addition to that, you can implement your own access controls, and comply with global standards like GDPR with crystal clear opt-in and opt-out mechanisms. With your conversations and customer data protected by enterprise-grade security and privacy protocols, you can focus just on delivering a great customer experience without worrying about data vulnerabilities.",
        },
        {
            question: "Does WhatsApp Team Inbox Software support message automation and templates?",
            answer: "Yes, our WhatsApp Team Inbox software offers a robust set of automation features that let you create message templates, chatbots, Broadcast, and auto-responses for common scenarios. You can set up responses that trigger based on specific keywords, and build end-to-end conversation flows. This means your team can save time on repetitive tasks while maintaining a personal touch and building solid relationships with prospects and customers alike.",
        },
        {
            question: "Can I track and analyze team performance through WhatsApp Team Inbox Software?",
            answer: "Of course, our WhatsApp Team Inbox’s analytics dashboards can help you monitor metrics like response times, resolution rates, and agent productivity in real-time. These insights will empower businesses to optimize staffing and improve conversions and resolution rates by identifying the areas where the customer-facing teams need to be trained.",
        },
        {
            question: "Is WhatsApp Team Inbox Software suitable for businesses of all sizes, from small to large enterprises?",
            answer: "Absolutely, yes. Gallabox’s WhatsApp Team Inbon is purpose-built to help growing businesses scale further without the pain of increasing headcount or inbound volume. Small teams and solopreneurs love our user-friendly interface, messaging automations, and high utility Broadcast features. While larger businesses have made best use of our advanced GenAI features, bot builders, and customizations. With flexible pricing plans and an always on customer success team, Gallabox offers something for everyone.",
        },
        {
            question: "Can I use WhatsApp Team Inbox Software for managing customer service inquiries across multiple channels?",
            answer: "No. Any WhatsApp Team Inbox software is meant to drive conversations only to your WhatsApp Business account. However, tools like Gallabox are close to bridging that gap with workarounds like Pabbly and Zapier to build bridges between WhatsApp and other channels. Soon, Gallabox will also have omnichannel capabilities with Instagram DMs and Facebook Messenger. Which means you can manage conversations from Facebook Messenger, Instagram DMs, web chat, and other channels alongside your WhatsApp messages - all from the same intuitive inbox. It's about meeting your customers wherever they prefer to connect with you.",
        },
        {
            question: "Does WhatsApp Team Inbox allow for real-time collaboration among team members?",
            answer: "Yes, our WhatsApp Team Inbox lets team members add private notes within customer conversations. It’s only visible internally, so tagging colleagues for inputs and co-piloting complex customer issues is a reality with Gallabox.",
        },
        {
            question: "Can I customize notifications and alerts for specific team members within WhatsApp Team Inbox?",
            answer: "Yes, each team member can personalize their notification preferences. From desktop alerts and emails to mobile push notifications, you can also set up alerts for high-priority messages or responses to an ongoing conversation, read receipts, and also failed messages. This flexibility ensures that everyone stays in the loop on the most important prospect or customer interactions.",
        },
        {
            question: "Does WhatsApp Team Inbox Software support rich media (images, videos, files, etc.)?",
            answer: "Yes, just like the native WhatsApp experience, our Whatsapp Team Inbox fully supports sharing and receiving images, videos, PDFs, location pins, voice notes, and other file types. You can also add buttons by creating message templates and configure “reply to” messages to nurture contacts. Once their replies land on your WhatsApp Team Inbox, one of your agents can either take over the conversation or let the bot collect more context. Apart from configuring a bot-to-human handoff, your agents can also override the chatbot and take over the conversation at any point.",
        },
        {
            question: "How do I ensure compliance with WhatsApp's policies when using Team Inbox Software?",
            answer: "We've got your back on this one! Our platform is built with WhatsApp's policies baked in, so many compliance aspects are handled automatically. We provide clear templates that follow WhatsApp's guidelines, built-in consent management, and regular updates whenever WhatsApp changes its policies. Our team also provides guidance on best practices to keep your business account in good standing.",
        },
        {
            question: "Can I tag and categorize conversations for easier management with WhatsApp Team Inbox?",
            answer: "Yes. Gallabox’s WhatsApp Team Inbox offers Conversation Tags, which are essentially labels that help you identify the nature of a customer conversation. You can create custom tags like 'Billing Question,' 'Product Support,' or 'Sales Lead' to categorize conversations. These tags are searchable and can be used to generate actionable insights on the nature of requests that come in.",
        },

        {
            question: "Does WhatsApp Team Inbox Software support analytics and reporting on customer interactions?",
            answer: "Yes, Gallabox’s Whatsapp Team Inbox provides analytics that go beyond basic metrics. You get comprehensive dashboards that uncover KPIs and analytics by Bot Sessions, Broadcasts, User Initiated Conversations, Business Initiated Conversations, and WhatsApp Template Messages, to name a few. There are a lot more nitty gritties you can drill down into to build a culture of data-backed decision making in your sales, marketing, and support teams.",
        },
    ],
};

const paymentSectionData: PaymentSectionDataProps[] = [
    {
        imageUrl: "https://assets.gallabox.com/gb-home/whatsapp-shared-inbox/gallabox-home.png",
        title: "Connect your WhatsApp Business account",
        description:
            "Sign up for a Gallabox account and connect your WhatsApp Business API number. Head to Users, Teams, and Roles to start setting up your team with access-control. ",
    },
    {
        imageUrl: "https://assets.gallabox.com/gb-home/whatsapp-shared-inbox/qouick-checklist.png",
        title: "Open conversations and organize your team",
        description:
            "After adding users, permissions, and team structures, head to the Conversations tab on the right pane. Once you set it up, head to the Conversations tab on the left pane.",
    },
    {
        imageUrl: "https://assets.gallabox.com/gb-home/whatsapp-shared-inbox/conversation-dashboard.png",
        title: "Customize your shared inbox",
        description: "Set up assignment rules, build bot workflows, add product catalogs, create canned replies, and configure notifications to match your team's needs.",
    },
    {
        imageUrl: "https://assets.gallabox.com/gb-home/whatsapp-shared-inbox/analytics-dashboard.png",
        title: "Keep close tabs on agent performance",
        description: "Start handling live customer conversations on Gallabox’s WhatsApp Team Inbox and use our analytics to continuously improve your team's performance.",
    },
];

const defaultFAQData: PaymentProcess = {
    title: "How to get started with the WhatsApp Team Inbox software",
    ProcessSteps: [
        {
            imageUrl: "https://assets.gallabox.com/gb-home/whatsapp-shared-inbox/gallabox-home.png",
            title: "Connect your WhatsApp Business account",
            description:
                "Sign up for a Gallabox account and connect your WhatsApp Business API number. Head to Users, Teams, and Roles to start setting up your team with access-control. ",
        },
        {
            imageUrl: "https://assets.gallabox.com/gb-home/whatsapp-shared-inbox/qouick-checklist.png",
            title: "Open conversations and organize your team",
            description:
                "After adding users, permissions, and team structures, head to the Conversations tab on the right pane. Once you set it up, head to the Conversations tab on the left pane.",
        },
        {
            imageUrl: "https://assets.gallabox.com/gb-home/whatsapp-shared-inbox/conversation-dashboard.png",
            title: "Customize your shared inbox",
            description: "Set up assignment rules, build bot workflows, add product catalogs, create canned replies, and configure notifications to match your team's needs.",
        },
        {
            imageUrl: "https://assets.gallabox.com/gb-home/whatsapp-shared-inbox/analytics-dashboard.png",
            title: "Keep close tabs on agent performance",
            description: "Start handling live customer conversations on Gallabox’s WhatsApp Team Inbox and use our analytics to continuously improve your team's performance.",
        },
    ],
};

const SharedTeamInbox: React.FC = () => {
    return (
        <NewLayout
            description="Help your team handle more qualified leads and customer messages than ever with Gallabox's AI-powered WhatsApp Team Inbox Software."
            title="WhatsApp Team Inbox Software for Efficient Teams"
            canonical="https://gallabox.com/whatsapp-shared-inbox"
            ogAttributes={{ image: "https://assets.gallabox.com/gb-home/whatsapp-shared-inbox/shared-team-inbox-og.png" }}
            schema={{
                heroOrOgImageAlt: "whatsapp-shared-inbox-image",
                heroOrOgImageDescription: "Manage 2X more business chats with WhatsApp Team Inbox software",
                heroOrOgImageUrl: "https://assets.gallabox.com/gb-home/whatsapp-shared-inbox/shared-team-inbox-hero-image.png",
                softwareFeatureName: "whatsapp-shared-inbox",
                faq: faqData.faqItems,
                howTo: { howtoSteps: ProblemSectionData.problemSectionCard },
            }}
        >
            <Herosection herosection={herosectionData} />
            <section className={`${container_classname} mt-10 mb-20`}>
                <h3 className="text-center text-lg font-normal py-5">TRUSTED BY 10,000+ BUSINESSES</h3>
                <ClientLogos />
            </section>
            <h2 className="md:text-6xl text-3xl font-semibold md:mb-12 mb-6 max-w-7xl mx-auto px-5 text-left" style={{ fontFamily: "Degular Display" }}>
                Key features and functionalities of WhatsApp Team Inbox software
            </h2>
            <FeatureInfoCard {...featureInfo1} />
            <FeatureInfoCard {...featureInfo2} />
            <FeatureInfoCard {...featureInfo3} />
            <ProblemSectionCard problemSection={ProblemSectionData} />
            <PaymentProcessCard title="How to get started with the WhatsApp Team Inbox software" paymentSectionData={paymentSectionData} backgroundColor="white" />
            <PaymentStepsAccordion paymentStepsData={defaultFAQData} />
            <CaseStudyFeatureSection features={caseStudyFeatureCardData} />
            <FAQSection faqData={faqData} />
            <IntegrationSection isLight={true} />
            <AwardsSection />
            <CTASection title="Enable your agents to sell and support faster with an intuitive WhatsApp Team Inbox" primaryButtonText="Try Gallabox for free" />
        </NewLayout>
    );
};

export default SharedTeamInbox;
